import React, { useRef, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Canvas, useFrame, useThree } from '@react-three/fiber';
import {
    Stars,
    Environment,
    Float,
    Text,
    ScrollControls,
    Scroll,
    useScroll,
    Billboard,
    PerspectiveCamera,
    Trail,
    useTexture
} from '@react-three/drei';
import { Vector3, MathUtils, Color, Mesh, Group, PerspectiveCamera as ThreePerspectiveCamera } from 'three';
import {
    Button,
    Box,
    Typography,
    Container,
    Grid,
    useTheme,
    Tabs,
    Tab,
    Tooltip,
    Paper,
    Divider,
    keyframes
} from '@mui/material';
import gsap from 'gsap';

// Import custom components and hooks
import HeaderComponent from '../components/common/HeaderComponent';
import ImpactTabs from '../components/common/ImpactTabs';
import ImpactStatsCard from '../components/common/ImpactStatsCard';
import useImpactStats, { ImpactTabType } from '../hooks/useImpactStats';

// Add animation keyframes for glowing effects
const glowPulse = keyframes`
  0% { box-shadow: 0 0 10px rgba(65, 177, 225, 0.6); }
  50% { box-shadow: 0 0 25px rgba(65, 177, 225, 0.9); }
  100% { box-shadow: 0 0 10px rgba(65, 177, 225, 0.6); }
`;

// EarthComponent for the 3D earth globe with enhanced visuals
const EarthComponent: React.FC<{ position?: [number, number, number]; scale?: number }> = ({ position = [0, 0, 0], scale = 1 }) => {
    const earthRef = useRef<Mesh>(null);
    const earthCloudRef = useRef<Mesh>(null);
    const atmosphereRef = useRef<Mesh>(null);
    const theme = useTheme();

    // Add rotation animation with GSAP
    useEffect(() => {
        if (earthRef.current) {
            gsap.to(earthRef.current.rotation, {
                y: Math.PI * 2,
                duration: 40,
                repeat: -1,
                ease: "none"
            });
        }
        if (earthCloudRef.current) {
            gsap.to(earthCloudRef.current.rotation, {
                y: Math.PI * 2,
                duration: 30,
                repeat: -1,
                ease: "none"
            });
        }

        // Add pulsing glow effect to atmosphere
        if (atmosphereRef.current) {
            gsap.to(atmosphereRef.current.material, {
                opacity: 0.2,
                duration: 2,
                repeat: -1,
                yoyo: true,
                ease: "sine.inOut"
            });
        }
    }, []);

    useFrame(({ clock }) => {
        // Add subtle wobble to earth
        if (earthRef.current) {
            earthRef.current.rotation.x = Math.sin(clock.getElapsedTime() * 0.2) * 0.02;
        }
    });

    return (
        <group position={position} scale={scale}>
            {/* Earth sphere */}
            <mesh ref={earthRef}>
                <sphereGeometry args={[2, 64, 64]} />
                <meshPhongMaterial
                    color={theme.palette.primary.main}
                    emissive={theme.palette.primary.dark}
                    emissiveIntensity={0.5}
                    specular={new Color('#3f88c5')}
                    shininess={25}
                />
            </mesh>

            {/* Cloud layer */}
            <mesh ref={earthCloudRef} scale={1.03}>
                <sphereGeometry args={[2, 32, 32]} />
                <meshPhongMaterial
                    color={theme.palette.secondary.light}
                    transparent
                    opacity={0.4}
                    depthWrite={false}
                />
            </mesh>

            {/* Add enhanced glowing atmosphere */}
            <mesh ref={atmosphereRef} scale={1.18}>
                <sphereGeometry args={[2, 64, 64]} />
                <meshBasicMaterial
                    color={new Color('#1a4172')}
                    transparent
                    opacity={0.15}
                    side={2} // Both sides
                />
            </mesh>

            {/* Add outer glow rim */}
            <mesh scale={1.25}>
                <sphereGeometry args={[2, 32, 32]} />
                <meshBasicMaterial
                    color={new Color('#41b1e1')}
                    transparent
                    opacity={0.05}
                    side={2}
                />
            </mesh>

            {/* Energy visualization points */}
            <EnergyPoints count={60} earthRadius={2.05} />
        </group>
    );
};

// Enhanced Energy points that appear around the earth
const EnergyPoints: React.FC<{ count: number; earthRadius: number }> = ({ count, earthRadius }) => {
    const group = useRef<Group>(null);
    const points = useRef<Vector3[]>([]);
    const theme = useTheme();

    // Generate random positions on sphere surface
    useEffect(() => {
        if (!group.current) return;

        points.current = [];
        for (let i = 0; i < count; i++) {
            const phi = Math.acos(-1 + (2 * i) / count);
            const theta = Math.sqrt(count * Math.PI) * phi;

            const x = earthRadius * Math.cos(theta) * Math.sin(phi);
            const y = earthRadius * Math.sin(theta) * Math.sin(phi);
            const z = earthRadius * Math.cos(phi);

            const point = new Vector3(x, y, z);
            points.current.push(point);
        }
    }, [count, earthRadius]);

    useFrame(({ clock }) => {
        const time = clock.getElapsedTime();

        if (group.current && group.current.children.length > 0) {
            group.current.children.forEach((child: any, i: number) => {
                // Enhanced pulse effect with variation
                const scale = 0.5 + Math.sin(time * (1.5 + i * 0.05) + i) * 0.25;
                child.scale.set(scale, scale, scale);

                // Make some points float slightly away from the surface
                const distance = 1.0 + Math.sin(time * 0.5 + i * 0.2) * 0.05;
                if (i < points.current.length) {
                    const pos = points.current[i].clone().normalize().multiplyScalar(earthRadius * distance);
                    child.position.set(pos.x, pos.y, pos.z);
                }
            });
        }
    });

    return (
        <group ref={group}>
            {Array.from({ length: count }).map((_, i) => {
                // Get the vector position or use default
                const position = i < points.current.length ?
                    [points.current[i].x, points.current[i].y, points.current[i].z] as [number, number, number] :
                    [0, 0, 0] as [number, number, number];

                // More varied colors for energy points
                const colorOptions = [
                    theme.palette.success.main,
                    theme.palette.info.main,
                    theme.palette.warning.main,
                    theme.palette.error.main,
                    '#41b1e1', // Cyan
                    '#9c27b0', // Purple
                    '#ff9800', // Orange
                ];

                const color = colorOptions[i % colorOptions.length];

                // Vary the size slightly
                const size = 0.04 + Math.random() * 0.04;

                return (
                    <mesh key={i} position={position}>
                        <sphereGeometry args={[size, 16, 16]} />
                        <meshBasicMaterial
                            color={color}
                            transparent
                            opacity={0.9}
                        />
                    </mesh>
                );
            })}
        </group>
    );
};

interface AnimatedTextProps {
    position: [number, number, number];
    text: string;
    fontSize?: number;
    color?: string;
}

// Animated text component
const AnimatedText: React.FC<AnimatedTextProps> = ({ position, text, fontSize = 0.5, color = '#ffffff' }) => {
    const textRef = useRef<Mesh>(null);
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { viewport } = useThree();

    useFrame(({ clock }) => {
        if (textRef.current) {
            // Gentle floating animation
            textRef.current.position.y = position[1] + Math.sin(clock.getElapsedTime() * 0.5) * 0.1;
        }
    });

    return (
        <Float speed={5} rotationIntensity={0.2} floatIntensity={0.2}>
            <Text
                ref={textRef}
                position={position}
                fontSize={fontSize}
                color={color}
                anchorX="center"
                anchorY="middle"
                characters="abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!"
            >
                {text}
            </Text>
        </Float>
    );
};

// Logo component
const LogoImage = ({ position }: { position: [number, number, number] }) => {
    const texture = useTexture('/EcoFusion_Logo.png');

    return (
        <Billboard
            position={position}
            follow={true}
            lockX={false}
            lockY={false}
            lockZ={false}
        >
            <mesh>
                <planeGeometry args={[3, 1.5]} />
                <meshBasicMaterial transparent opacity={1} side={2} map={texture} />
            </mesh>
        </Billboard>
    );
};

// 3D Scene component
const Scene: React.FC = () => {
    const theme = useTheme();
    const navigate = useNavigate();
    const scroll = useScroll();
    const sceneRef = useRef<Group>(null);
    const cameraPosRef = useRef<Vector3>(new Vector3(0, 0, 8));
    const [hovered, setHovered] = useState<string | null>(null);
    const [activeSection, setActiveSection] = useState<string | null>(null);

    // Adjust scales to make assets smaller and all visible at once
    const assetScale = 0.65;

    // Animation for camera position based on scroll
    useFrame(({ camera }) => {
        if (sceneRef.current) {
            // Get scroll progress (0-1)
            const scrollProgress = scroll.offset;

            // Enhanced scroll effects
            // Rotate based on scroll
            sceneRef.current.rotation.y = scrollProgress * Math.PI * 2;

            // Move camera based on scroll - more dynamic movement
            cameraPosRef.current.z = 12 - scrollProgress * 6; // Move closer as we scroll
            cameraPosRef.current.y = scrollProgress * 3; // Move up slightly as we scroll

            // Apply camera position with smooth damping
            camera.position.lerp(cameraPosRef.current, 0.05);

            // Add subtle sway to the entire scene based on scroll
            sceneRef.current.rotation.x = Math.sin(scrollProgress * Math.PI) * 0.1;
        }
    });

    // Interactive section handlers
    const handleSectionHover = (section: string | null) => {
        setHovered(section);
    };

    const handleSectionClick = (section: string) => {
        setActiveSection(section);
        // After a delay, navigate to the corresponding page
        setTimeout(() => {
            switch (section) {
                case 'land':
                    navigate('/soil');
                    break;
                case 'water':
                    navigate('/water');
                    break;
                case 'energy':
                    navigate('/energy-potential');
                    break;
                default:
                    break;
            }
        }, 1000);
    };

    return (
        <group ref={sceneRef}>
            {/* Central Earth */}
            <EarthComponent position={[0, 0, 0]} scale={1} />

            {/* Logo image instead of text */}
            <LogoImage position={[0, 3.5, 0]} />

            <AnimatedText
                position={[0, 2.5, 0]}
                text="Sustainable Solutions for Our Planet"
                fontSize={0.5}
                color={theme.palette.secondary.main}
            />

            {/* LAND SECTION - Left side */}
            <group
                position={[-5, -1, 2]}
                scale={assetScale}
                onPointerOver={() => handleSectionHover('land')}
                onPointerOut={() => handleSectionHover(null)}
                onClick={() => handleSectionClick('land')}
            >
                <Float speed={2} rotationIntensity={0.4} floatIntensity={0.5}>
                    {/* 3D Land object */}
                    <group>
                        {/* Terrain base */}
                        <mesh position={[0, -0.5, 0]} scale={hovered === 'land' ? 1.1 : 1}>
                            <cylinderGeometry args={[1.5, 1.5, 0.2, 32]} />
                            <meshStandardMaterial
                                color={theme.palette.success.main}
                                roughness={0.8}
                                metalness={0.2}
                                emissive={theme.palette.success.light}
                                emissiveIntensity={hovered === 'land' ? 0.5 : 0.2}
                            />
                        </mesh>

                        {/* Mountains */}
                        <mesh position={[-0.5, 0, 0]} scale={hovered === 'land' ? 1.1 : 1}>
                            <coneGeometry args={[0.5, 1, 32]} />
                            <meshStandardMaterial
                                color={theme.palette.success.dark}
                                roughness={0.9}
                                emissive={theme.palette.success.dark}
                                emissiveIntensity={hovered === 'land' ? 0.3 : 0.1}
                            />
                        </mesh>

                        <mesh position={[0.2, 0, 0.4]} scale={hovered === 'land' ? 1.1 : 1}>
                            <coneGeometry args={[0.3, 0.7, 32]} />
                            <meshStandardMaterial
                                color={theme.palette.success.dark}
                                roughness={0.9}
                                emissive={theme.palette.success.dark}
                                emissiveIntensity={hovered === 'land' ? 0.3 : 0.1}
                            />
                        </mesh>

                        {/* Trees with improved visuals */}
                        {Array.from({ length: 12 }).map((_, i) => {
                            const angle = (i / 12) * Math.PI * 2;
                            const r = 1;
                            const x = Math.cos(angle) * r;
                            const z = Math.sin(angle) * r;
                            const scaleVar = 0.7 + Math.random() * 0.6;
                            return (
                                <group key={i} position={[x, 0, z]} scale={[scaleVar, scaleVar, scaleVar] as [number, number, number]}>
                                    <mesh position={[0, 0, 0]}>
                                        <cylinderGeometry args={[0.05, 0.07, 0.2, 8]} />
                                        <meshStandardMaterial color={"#8B4513"} roughness={0.7} />
                                    </mesh>
                                    <mesh position={[0, 0.25, 0]}>
                                        <coneGeometry args={[0.2, 0.5, 8]} />
                                        <meshStandardMaterial
                                            color={"#228B22"}
                                            emissive={"#228B22"}
                                            emissiveIntensity={hovered === 'land' ? 0.3 : 0.1}
                                        />
                                    </mesh>
                                </group>
                            );
                        })}
                    </group>

                    <Billboard position={[0, 1.2, 0]}>
                        <Text fontSize={0.4} color={theme.palette.success.main} fontWeight="bold">
                            LAND
                        </Text>
                    </Billboard>

                    {/* Information panel */}
                    {hovered === 'land' && (
                        <Billboard position={[0, -1.5, 0]}>
                            <mesh>
                                <planeGeometry args={[3, 1.2]} />
                                <meshBasicMaterial
                                    color={theme.palette.background.paper}
                                    transparent
                                    opacity={0.8}
                                />
                            </mesh>
                            <Text
                                position={[0, 0.3, 0.01]}
                                fontSize={0.18}
                                color={theme.palette.text.primary}
                                maxWidth={2.8}
                                textAlign="center"
                            >
                                Soil Monitoring & Sustainable Farming
                            </Text>
                            <Text
                                position={[0, 0, 0.01]}
                                fontSize={0.12}
                                color={theme.palette.text.secondary}
                                maxWidth={2.8}
                                textAlign="center"
                            >
                                Optimize soil health with real-time monitoring
                            </Text>
                            <Text
                                position={[0, -0.3, 0.01]}
                                fontSize={0.12}
                                color={theme.palette.text.secondary}
                                maxWidth={2.8}
                                textAlign="center"
                            >
                                Track nutrients, moisture, and get recommendations
                            </Text>
                        </Billboard>
                    )}
                </Float>
            </group>

            {/* WATER SECTION - Front */}
            <group
                position={[3, -2, 4]}
                scale={assetScale}
                onPointerOver={() => handleSectionHover('water')}
                onPointerOut={() => handleSectionHover(null)}
                onClick={() => handleSectionClick('water')}
            >
                <Float speed={3} rotationIntensity={0.5} floatIntensity={0.7}>
                    {/* 3D Water object with enhanced effects */}
                    <group>
                        {/* Water surface */}
                        <mesh position={[0, -0.1, 0]} scale={hovered === 'water' ? 1.1 : 1}>
                            <cylinderGeometry args={[1.5, 1.5, 0.2, 32]} />
                            <meshPhysicalMaterial
                                color={theme.palette.info.main}
                                roughness={0.1}
                                metalness={0.9}
                                clearcoat={1}
                                clearcoatRoughness={0.1}
                                transparent
                                opacity={0.9}
                                emissive={theme.palette.info.light}
                                emissiveIntensity={hovered === 'water' ? 0.6 : 0.3}
                            />
                        </mesh>

                        {/* Water ripples with animation */}
                        {Array.from({ length: 4 }).map((_, i) => (
                            <mesh
                                key={i}
                                position={[0, -0.05 + (i * 0.05), 0]}
                                scale={hovered === 'water' ? 1.1 - (i * 0.1) : 1 - (i * 0.1)}
                            >
                                <ringGeometry args={[1.3 - (i * 0.2), 1.5 - (i * 0.2), 32]} />
                                <meshPhysicalMaterial
                                    color={theme.palette.info.light}
                                    roughness={0}
                                    metalness={0.9}
                                    transparent
                                    opacity={0.4}
                                    emissive={theme.palette.info.main}
                                    emissiveIntensity={0.4}
                                />
                            </mesh>
                        ))}

                        {/* Water droplets with animation */}
                        {Array.from({ length: 8 }).map((_, i) => {
                            const angle = (i / 8) * Math.PI * 2;
                            const r = 0.8;
                            const x = Math.cos(angle) * r;
                            const z = Math.sin(angle) * r;
                            return (
                                <mesh
                                    key={i}
                                    position={[x, 0.3 + Math.sin(i) * 0.2, z]}
                                    scale={hovered === 'water' ? 1.2 : 1}
                                >
                                    <sphereGeometry args={[0.15, 16, 16]} />
                                    <meshPhysicalMaterial
                                        color={theme.palette.info.light}
                                        roughness={0}
                                        metalness={0.9}
                                        clearcoat={1}
                                        transparent
                                        opacity={0.8}
                                        emissive={theme.palette.info.main}
                                        emissiveIntensity={0.5}
                                    />
                                </mesh>
                            );
                        })}
                    </group>

                    <Billboard position={[0, 1.2, 0]}>
                        <Text fontSize={0.4} color={theme.palette.info.main} fontWeight="bold">
                            WATER
                        </Text>
                    </Billboard>

                    {/* Information panel */}
                    {hovered === 'water' && (
                        <Billboard position={[0, -1.5, 0]}>
                            <mesh>
                                <planeGeometry args={[3, 1.2]} />
                                <meshBasicMaterial
                                    color={theme.palette.background.paper}
                                    transparent
                                    opacity={0.8}
                                />
                            </mesh>
                            <Text
                                position={[0, 0.3, 0.01]}
                                fontSize={0.18}
                                color={theme.palette.text.primary}
                                maxWidth={2.8}
                                textAlign="center"
                            >
                                Water Quality & Conservation
                            </Text>
                            <Text
                                position={[0, 0, 0.01]}
                                fontSize={0.12}
                                color={theme.palette.text.secondary}
                                maxWidth={2.8}
                                textAlign="center"
                            >
                                Monitor water quality metrics in real-time
                            </Text>
                            <Text
                                position={[0, -0.3, 0.01]}
                                fontSize={0.12}
                                color={theme.palette.text.secondary}
                                maxWidth={2.8}
                                textAlign="center"
                            >
                                Get conservation tips and contamination alerts
                            </Text>
                        </Billboard>
                    )}
                </Float>
            </group>

            {/* ENERGY SECTION - Right side */}
            <group
                position={[-3, -3, -3]}
                scale={assetScale}
                onPointerOver={() => handleSectionHover('energy')}
                onPointerOut={() => handleSectionHover(null)}
                onClick={() => handleSectionClick('energy')}
            >
                <Float speed={4} rotationIntensity={0.3} floatIntensity={0.4}>
                    {/* 3D Energy objects with visual improvements */}
                    <group>
                        {/* Base platform */}
                        <mesh position={[0, -0.5, 0]} scale={hovered === 'energy' ? 1.1 : 1}>
                            <cylinderGeometry args={[1.5, 1.5, 0.2, 32]} />
                            <meshStandardMaterial
                                color={theme.palette.warning.main}
                                roughness={0.4}
                                metalness={0.6}
                                emissive={theme.palette.warning.light}
                                emissiveIntensity={hovered === 'energy' ? 0.5 : 0.2}
                            />
                        </mesh>

                        {/* Solar Panel with improved lighting */}
                        <group position={[-0.6, 0, 0.2]} scale={hovered === 'energy' ? 1.1 : 1} rotation={[0, Math.PI / 4, Math.PI / 6]}>
                            <mesh>
                                <boxGeometry args={[0.8, 0.05, 0.6]} />
                                <meshStandardMaterial color={"#1E1E1E"} />
                            </mesh>
                            {/* Solar cells with glow effect */}
                            {Array.from({ length: 8 }).map((_, i) => (
                                <mesh key={i} position={[(i % 4) * 0.2 - 0.3, 0.03, Math.floor(i / 4) * 0.2 - 0.1]}>
                                    <boxGeometry args={[0.18, 0.01, 0.18]} />
                                    <meshPhysicalMaterial
                                        color={"#0047AB"}
                                        roughness={0}
                                        metalness={1}
                                        emissive={"#4682B4"}
                                        emissiveIntensity={hovered === 'energy' ? 0.8 : 0.4}
                                    />
                                </mesh>
                            ))}
                        </group>

                        {/* Wind Turbine with rotation animation */}
                        <group position={[0.5, 0, -0.3]} scale={hovered === 'energy' ? 1.1 : 1}>
                            <mesh position={[0, 0.5, 0]}>
                                <cylinderGeometry args={[0.05, 0.1, 1, 8]} />
                                <meshStandardMaterial color={"#E0E0E0"} />
                            </mesh>
                            {/* Turbine blades - now rotating */}
                            <group position={[0, 1, 0]}>
                                {Array.from({ length: 3 }).map((_, i) => (
                                    <mesh
                                        key={i}
                                        position={[0, 0, 0]}
                                        rotation={[0, 0, (i * Math.PI * 2) / 3]}
                                        userData={{ rotationSpeed: 0.01 }}
                                    >
                                        <boxGeometry args={[0.05, 0.5, 0.02]} />
                                        <meshStandardMaterial color={"#FFFFFF"} />
                                    </mesh>
                                ))}
                            </group>
                        </group>

                        {/* Energy pulses with enhanced glow */}
                        {Array.from({ length: 10 }).map((_, i) => {
                            const angle = (i / 10) * Math.PI * 2;
                            const r = 1.2;
                            const x = Math.cos(angle) * r;
                            const z = Math.sin(angle) * r;
                            return (
                                <mesh
                                    key={i}
                                    position={[x, 0.2 + Math.sin(i * 0.5) * 0.3, z]}
                                    scale={hovered === 'energy' ? 1.2 : 1}
                                >
                                    <sphereGeometry args={[0.08, 16, 16]} />
                                    <meshStandardMaterial
                                        color={theme.palette.warning.light}
                                        transparent
                                        opacity={0.8}
                                        emissive={theme.palette.warning.main}
                                        emissiveIntensity={0.6}
                                    />
                                </mesh>
                            );
                        })}
                    </group>

                    <Billboard position={[0, 1.2, 0]}>
                        <Text fontSize={0.4} color={theme.palette.warning.main} fontWeight="bold">
                            ENERGY
                        </Text>
                    </Billboard>

                    {/* Information panel */}
                    {hovered === 'energy' && (
                        <Billboard position={[0, -1.5, 0]}>
                            <mesh>
                                <planeGeometry args={[3, 1.2]} />
                                <meshBasicMaterial
                                    color={theme.palette.background.paper}
                                    transparent
                                    opacity={0.8}
                                />
                            </mesh>
                            <Text
                                position={[0, 0.3, 0.01]}
                                fontSize={0.18}
                                color={theme.palette.text.primary}
                                maxWidth={2.8}
                                textAlign="center"
                            >
                                Renewable Energy Solutions
                            </Text>
                            <Text
                                position={[0, 0, 0.01]}
                                fontSize={0.12}
                                color={theme.palette.text.secondary}
                                maxWidth={2.8}
                                textAlign="center"
                            >
                                Explore global energy potential for your location
                            </Text>
                            <Text
                                position={[0, -0.3, 0.01]}
                                fontSize={0.12}
                                color={theme.palette.text.secondary}
                                maxWidth={2.8}
                                textAlign="center"
                            >
                                Calculate ROI and find optimal renewable solutions
                            </Text>
                        </Billboard>
                    )}
                </Float>
            </group>

            {/* Add additional content for long scrolling */}
            <group position={[0, -15, 0]}>
                <EarthComponent position={[0, 0, 0]} scale={0.7} />
                <Billboard position={[0, 3, 0]}>
                    <Text fontSize={0.8} color="#ffffff">
                        OUR MISSION
                    </Text>
                </Billboard>
                <Billboard position={[0, 2, 0]}>
                    <Text fontSize={0.4} color="#aaaaaa" maxWidth={10} textAlign="center">
                        EcoFusion brings together cutting-edge technology and environmental science to create a sustainable future.
                    </Text>
                </Billboard>
            </group>

            {/* Third section for long scrolling */}
            <group position={[0, -30, 0]}>
                <Billboard position={[0, 3, 0]}>
                    <Text fontSize={0.8} color="#ffffff">
                        JOIN US
                    </Text>
                </Billboard>
                <Billboard position={[0, 2, 0]}>
                    <Text fontSize={0.4} color="#aaaaaa" maxWidth={10} textAlign="center">
                        Together we can build a greener, more sustainable planet for future generations.
                    </Text>
                </Billboard>

                {/* Add visual elements for the final section */}
                <group position={[0, 0, 0]}>
                    <mesh position={[0, 0, 0]}>
                        <torusGeometry args={[3, 0.2, 16, 100]} />
                        <meshStandardMaterial
                            color={"#4CAF50"}
                            emissive={"#4CAF50"}
                            emissiveIntensity={0.5}
                        />
                    </mesh>

                    {/* Add dots around the torus */}
                    {Array.from({ length: 30 }).map((_, i) => {
                        const angle = (i / 30) * Math.PI * 2;
                        const r = 3;
                        const x = Math.cos(angle) * r;
                        const z = Math.sin(angle) * r;
                        return (
                            <mesh key={i} position={[x, 0, z]}>
                                <sphereGeometry args={[0.15, 16, 16]} />
                                <meshStandardMaterial
                                    color={"#2196F3"}
                                    emissive={"#2196F3"}
                                    emissiveIntensity={0.6}
                                />
                            </mesh>
                        );
                    })}
                </group>
            </group>

            {/* Environment lighting and stars */}
            <Stars radius={100} depth={50} count={8000} factor={4} saturation={0} fade speed={1} />
            <Environment preset="sunset" />

            {/* Global ambient light */}
            <ambientLight intensity={0.6} />

            {/* Directional main light */}
            <directionalLight position={[10, 10, 5]} intensity={1.2} castShadow />

            {/* Accent lights with higher intensity */}
            <pointLight position={[-10, 0, -10]} color={theme.palette.success.main} intensity={1.5} distance={20} />
            <pointLight position={[0, -10, 5]} color={theme.palette.info.main} intensity={1.5} distance={20} />
            <pointLight position={[10, 0, -10]} color={theme.palette.warning.main} intensity={1.5} distance={20} />
        </group>
    );
};

// UIOverlay component with improved styles and a single footer
const UIOverlay: React.FC = () => {
    const [selectedTab, setSelectedTab] = useState<ImpactTabType>('energy');
    const { impactStats, loading } = useImpactStats();
    const theme = useTheme();

    return (
        <Box
            sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                pointerEvents: 'none',
                zIndex: 10,
            }}
        >
            <HeaderComponent showLogo={true} />

            <Box
                sx={{
                    position: 'absolute',
                    bottom: '25px',
                    width: '100%',
                    padding: '0 24px',
                    zIndex: 100,
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }
                }}
            >
                <Box sx={{
                    gridColumn: { xs: '1', md: '1 / span 1' },
                    animation: `${glowPulse} 4s infinite ease-in-out`,
                    borderRadius: '10px',
                }}>
                    <ImpactTabs selectedTab={selectedTab} setSelectedTab={setSelectedTab} />
                    <ImpactStatsCard type={selectedTab} stats={impactStats?.[selectedTab]} loading={loading} />
                </Box>
            </Box>
        </Box>
    );
};

// Enhanced News Section Component with more attractive styling
const NewsSection: React.FC = () => {
    const theme = useTheme();
    const [newsItems, setNewsItems] = useState<any[]>(mockNewsData);
    const [activeCategory, setActiveCategory] = useState<string>('all');
    const [expanded, setExpanded] = useState<boolean>(false);

    // Filter news by category
    const filteredNews = activeCategory === 'all'
        ? newsItems
        : newsItems.filter(item => item.category === activeCategory);

    return (
        <Box
            sx={{
                position: 'absolute',
                bottom: expanded ? '0' : '-435px', // Start hidden below the viewport
                left: 0,
                right: 0,
                height: '450px',
                background: 'rgba(10,25,47,0.85)', // Darker, more saturated background 
                backdropFilter: 'blur(12px)', // Stronger blur
                transition: 'all 0.6s cubic-bezier(0.175, 0.885, 0.32, 1.275)', // More bouncy animation
                zIndex: 20,
                borderTopLeftRadius: 20,
                borderTopRightRadius: 20,
                boxShadow: '0 -10px 30px rgba(0,0,0,0.4), 0 -1px 0 rgba(255,255,255,0.1) inset', // Better shadow with inset highlight
                overflow: 'hidden'
            }}
        >
            {/* Toggle handle with improved styling */}
            <Box
                sx={{
                    position: 'absolute',
                    top: -40,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    width: '140px',
                    height: '40px',
                    background: 'rgba(10,25,47,0.85)',
                    backdropFilter: 'blur(12px)',
                    borderTopLeftRadius: 20,
                    borderTopRightRadius: 20,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    cursor: 'pointer',
                    boxShadow: '0 -5px 20px rgba(0,0,0,0.3), 0 -1px 0 rgba(255,255,255,0.1) inset',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                        background: 'rgba(20,40,70,0.9)',
                        transform: 'translateX(-50%) translateY(-5px)',
                    },
                    animation: `${glowPulse} 4s infinite ease-in-out`,
                }}
                onClick={() => setExpanded(!expanded)}
            >
                <Typography variant="body2"
                    sx={{
                        color: 'white',
                        fontWeight: 'bold',
                        textTransform: 'uppercase',
                        letterSpacing: '1px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1
                    }}
                >
                    {expanded ? '▼ Hide News' : '▲ Show News'}
                </Typography>
            </Box>

            <Container maxWidth="xl" sx={{ height: '100%', pt: 3, pb: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h5" sx={{
                        color: 'white',
                        fontWeight: 'bold',
                        textShadow: '0 2px 8px rgba(0,0,0,0.7)',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        letterSpacing: '1px'
                    }}>
                        <span className="material-symbols-outlined">feed</span>
                        SUSTAINABILITY NEWS
                    </Typography>

                    {/* Category filters with enhanced styling */}
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        {['all', 'energy', 'water', 'land', 'sustainability'].map(category => (
                            <Button
                                key={category}
                                variant={activeCategory === category ? 'contained' : 'outlined'}
                                color={
                                    category === 'energy' ? 'warning' :
                                        category === 'water' ? 'info' :
                                            category === 'land' ? 'success' :
                                                'primary'
                                }
                                size="small"
                                onClick={() => setActiveCategory(category)}
                                sx={{
                                    textTransform: 'capitalize',
                                    minWidth: '85px',
                                    fontWeight: 'bold',
                                    borderRadius: '20px',
                                    boxShadow: activeCategory === category ? '0 4px 10px rgba(0,0,0,0.3)' : 'none',
                                    '&:hover': {
                                        transform: 'translateY(-2px)',
                                        boxShadow: '0 6px 12px rgba(0,0,0,0.4)'
                                    },
                                    transition: 'all 0.2s ease'
                                }}
                            >
                                {category}
                            </Button>
                        ))}
                    </Box>
                </Box>

                {/* News cards with improved styling */}
                <Grid container spacing={2} sx={{ maxHeight: '370px', overflowY: 'auto', pb: 2, px: 1 }}>
                    {filteredNews.map(news => (
                        <Grid key={news.id} sx={{ gridColumn: { xs: 'span 12', sm: 'span 6', md: 'span 4', lg: 'span 2.4' } }}>
                            <Box
                                sx={{
                                    height: '320px',
                                    borderRadius: '16px',
                                    overflow: 'hidden',
                                    position: 'relative',
                                    boxShadow: '0 10px 30px rgba(0,0,0,0.4)',
                                    transition: 'all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
                                    transform: 'perspective(1000px) rotateX(0deg)',
                                    '&:hover': {
                                        transform: 'perspective(1000px) rotateX(5deg) translateY(-15px)',
                                        boxShadow: '0 20px 40px rgba(0,0,0,0.5)',
                                        '& .news-image': {
                                            transform: 'scale(1.1)',
                                        }
                                    }
                                }}
                            >
                                {/* News image with hover effect */}
                                <Box
                                    className="news-image"
                                    sx={{
                                        height: '150px',
                                        width: '100%',
                                        backgroundImage: `url(${news.imageUrl})`,
                                        backgroundSize: 'cover',
                                        backgroundPosition: 'center',
                                        transition: 'transform 0.6s ease',
                                        '&::after': {
                                            content: '""',
                                            position: 'absolute',
                                            top: 0,
                                            left: 0,
                                            width: '100%',
                                            height: '150px',
                                            background: 'linear-gradient(to bottom, rgba(0,0,0,0.1), rgba(0,0,0,0.5))',
                                        }
                                    }}
                                >
                                    {/* Enhanced category badge */}
                                    <Box
                                        sx={{
                                            position: 'absolute',
                                            top: 10,
                                            right: 10,
                                            py: 0.5,
                                            px: 1.5,
                                            borderRadius: '20px',
                                            backgroundColor:
                                                news.category === 'energy' ? theme.palette.warning.main :
                                                    news.category === 'water' ? theme.palette.info.main :
                                                        news.category === 'land' ? theme.palette.success.main :
                                                            theme.palette.primary.main,
                                            color: 'white',
                                            fontWeight: 'bold',
                                            fontSize: '0.7rem',
                                            textTransform: 'uppercase',
                                            boxShadow: '0 4px 8px rgba(0,0,0,0.3)',
                                            zIndex: 1,
                                            letterSpacing: '0.5px'
                                        }}
                                    >
                                        {news.category}
                                    </Box>
                                </Box>

                                {/* News content with enhanced styling */}
                                <Box sx={{
                                    p: 2,
                                    bgcolor: 'background.paper',
                                    height: '170px',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    borderTop: `3px solid ${news.category === 'energy' ? theme.palette.warning.main :
                                        news.category === 'water' ? theme.palette.info.main :
                                            news.category === 'land' ? theme.palette.success.main :
                                                theme.palette.primary.main
                                        }`
                                }}>
                                    <Typography variant="subtitle1" component="h3"
                                        sx={{
                                            fontWeight: 'bold',
                                            mb: 1,
                                            fontSize: '0.95rem',
                                            lineHeight: 1.2,
                                            display: '-webkit-box',
                                            overflow: 'hidden',
                                            WebkitBoxOrient: 'vertical',
                                            WebkitLineClamp: 2,
                                            color: theme.palette.text.primary
                                        }}
                                    >
                                        {news.title}
                                    </Typography>

                                    <Typography variant="body2" color="text.secondary"
                                        sx={{
                                            mb: 1,
                                            display: '-webkit-box',
                                            overflow: 'hidden',
                                            WebkitBoxOrient: 'vertical',
                                            WebkitLineClamp: 3,
                                            fontSize: '0.8rem'
                                        }}
                                    >
                                        {news.description}
                                    </Typography>

                                    <Box sx={{
                                        mt: 'auto',
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        borderTop: '1px solid rgba(0, 0, 0, 0.08)',
                                        pt: 1
                                    }}>
                                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 'medium' }}>
                                            {new Date(news.date).toLocaleDateString()}
                                        </Typography>
                                        <Typography variant="caption"
                                            sx={{
                                                color: news.category === 'energy' ? theme.palette.warning.main :
                                                    news.category === 'water' ? theme.palette.info.main :
                                                        news.category === 'land' ? theme.palette.success.main :
                                                            theme.palette.primary.main,
                                                fontWeight: 'bold'
                                            }}
                                        >
                                            {news.source}
                                        </Typography>
                                    </Box>
                                </Box>

                                {/* Overlay link */}
                                <Box
                                    component="a"
                                    href={news.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    sx={{
                                        position: 'absolute',
                                        top: 0,
                                        left: 0,
                                        right: 0,
                                        bottom: 0,
                                        zIndex: 1,
                                        cursor: 'pointer'
                                    }}
                                />
                            </Box>
                        </Grid>
                    ))}
                </Grid>
            </Container>
        </Box>
    );
};

// News item interface
interface NewsItem {
    id: string;
    title: string;
    description: string;
    source: string;
    imageUrl: string;
    url: string;
    category: 'energy' | 'water' | 'land' | 'sustainability';
    date: string;
}

// Mock news data (in real app, you'd fetch this from an API)
const mockNewsData: NewsItem[] = [
    {
        id: '1',
        title: 'New Solar Technology Increases Efficiency by 40%',
        description: 'Researchers have developed a breakthrough photovoltaic cell that significantly improves energy capture.',
        source: 'Clean Energy Today',
        imageUrl: 'https://images.unsplash.com/photo-1508514177221-188b1cf16e9d?ixlib=rb-4.0.3&auto=format&fit=crop&w=300&q=80',
        url: '#',
        category: 'energy',
        date: '2023-04-02'
    },
    {
        id: '2',
        title: 'Global Water Conservation Efforts Show Promising Results',
        description: 'International initiatives to preserve freshwater resources are beginning to show measurable impacts.',
        source: 'Water Resources Journal',
        imageUrl: 'https://images.unsplash.com/photo-1486325212027-8081e485255e?ixlib=rb-4.0.3&auto=format&fit=crop&w=300&q=80',
        url: '#',
        category: 'water',
        date: '2023-04-01'
    },
    {
        id: '3',
        title: 'Sustainable Farming Practices Restore Soil Health',
        description: 'Farmers using regenerative agriculture are reporting significant improvements in soil quality and yields.',
        source: 'Modern Agriculture',
        imageUrl: 'https://images.unsplash.com/photo-1593561941665-2400a7590e8c?ixlib=rb-4.0.3&auto=format&fit=crop&w=300&q=80',
        url: '#',
        category: 'land',
        date: '2023-03-29'
    },
    {
        id: '4',
        title: 'EcoFusion Partners with Leading Sustainable Brands',
        description: 'New collaborations aim to accelerate adoption of eco-friendly technologies globally.',
        source: 'Green Business Quarterly',
        imageUrl: 'https://images.unsplash.com/photo-1551649001-7a21409a2c87?ixlib=rb-4.0.3&auto=format&fit=crop&w=300&q=80',
        url: '#',
        category: 'sustainability',
        date: '2023-03-28'
    },
    {
        id: '5',
        title: 'Hydro Power Innovations Minimize Environmental Impact',
        description: 'Next-generation turbines designed to protect aquatic ecosystems while harvesting energy.',
        source: 'Renewable Review',
        imageUrl: 'https://images.unsplash.com/photo-1468182563297-31c44a4802ea?ixlib=rb-4.0.3&auto=format&fit=crop&w=300&q=80',
        url: '#',
        category: 'energy',
        date: '2023-03-25'
    }
];

// Main LandingPage component - removed footer from here since it's in UIOverlay
const LandingPage: React.FC = () => {
    return (
        <Box
            sx={{
                position: 'fixed',
                top: 0,
                left: 0,
                width: '100vw',
                height: '100vh',
                overflow: 'hidden',
                background: 'linear-gradient(to bottom, #0a192f, #172a45)',
            }}
        >
            <Canvas shadows dpr={[1, 2]}>
                <PerspectiveCamera makeDefault position={[0, 0, 12]} fov={50} />
                <ScrollControls pages={5} distance={1} damping={0.3}>
                    <Scroll>
                        <Scene />
                    </Scroll>
                </ScrollControls>
            </Canvas>

            <UIOverlay />
            <NewsSection />
        </Box>
    );
};

export default LandingPage; 